/**
 * Simple interest calculations + inverse solvers.
 * For compound interest, see /src/lib/calculators/compoundInterest.ts.
 */

export type SolveFor = "interest" | "principal" | "rate" | "time";

export interface SimpleInput {
  principal?: number;
  annualRatePct?: number;
  years?: number;
  interest?: number;
}

export interface SimpleResult {
  principal: number;
  annualRatePct: number;
  years: number;
  interest: number;
  total: number; // principal + interest
}

/**
 * Simple interest: SI = (P × R × T) / 100; A = P + SI
 * Inverse forms used when one variable is the unknown.
 */
export function solveSimple(solveFor: SolveFor, input: SimpleInput): SimpleResult | null {
  const { principal, annualRatePct, years, interest } = input;
  let P = principal ?? 0;
  let R = annualRatePct ?? 0;
  let T = years ?? 0;
  let I = interest ?? 0;

  switch (solveFor) {
    case "interest":
      if (P <= 0 || R < 0 || T <= 0) return null;
      I = (P * R * T) / 100;
      break;
    case "principal":
      if (I <= 0 || R <= 0 || T <= 0) return null;
      P = (I * 100) / (R * T);
      break;
    case "rate":
      if (P <= 0 || I <= 0 || T <= 0) return null;
      R = (I * 100) / (P * T);
      break;
    case "time":
      if (P <= 0 || I <= 0 || R <= 0) return null;
      T = (I * 100) / (P * R);
      break;
  }

  return {
    principal: round2(P),
    annualRatePct: round4(R),
    years: round4(T),
    interest: round2(I),
    total: round2(P + I),
  };
}

export interface YearRow {
  year: number;
  interestThisYear: number;
  cumulativeInterest: number;
  balance: number; // principal + cumulative interest
}

/** Year-by-year linear accumulation for simple interest. */
export function simpleSchedule(principal: number, annualRatePct: number, years: number): YearRow[] {
  const totalYears = Math.max(1, Math.ceil(years));
  const perYear = (principal * annualRatePct) / 100;
  const rows: YearRow[] = [];
  let cum = 0;
  for (let y = 1; y <= totalYears; y++) {
    // For final fractional year, prorate
    const frac = y <= years ? 1 : Math.max(0, years - (y - 1));
    const thisYr = perYear * frac;
    cum += thisYr;
    rows.push({
      year: y,
      interestThisYear: round2(thisYr),
      cumulativeInterest: round2(cum),
      balance: round2(principal + cum),
    });
  }
  return rows;
}

function round2(n: number): number { return Math.round(n * 100) / 100; }
function round4(n: number): number { return Math.round(n * 10000) / 10000; }
